import React, { useState } from "react";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import SquireCheckbox from "components/Checkbox/SquireCheckobx";
import { useNavigate } from "react-router";
import locations from "../locations.json";
import { buildResultsPageUrl } from "utils/formatUrl";
import { formatPropertyType } from "utils/searchFilter";
import useSearch from "hooks/useSearch";
import useSearchFilter from "hooks/useSearchFilter";
import useProperties from "hooks/useProperties";
import ButtonSpinner from "components/Loading/ButtonSpinner";
import { capitalizeWord } from "utils/commonUtils";
import GeoJSONMap from "./GeoJSONMap";
import DrawMap from "./DrawMap";
import BaseMap from "components/Map/BaseMap";
import { useMediaQuery } from "react-responsive";

const MapLeftSidebar = ({
  mapType,
  selectedLocations,
  setSelectedLocations,
  toggleLocationSelection,
  handleMapLocationSelect,
  setDrawLocationName,
  handleLocationRemove,
}) => {
  const navigation = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const { selectedPropertyType, propertyTypeDetails, closeSearchDropdown } =
    useSearch();
  const { sortBy } = useSearchFilter();
  const { loading, error, searchProperties } = useProperties();
  const [viewMap, setViewMap] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const handleSelectLocation = (location) => {
    setSelectedLocations((prevSelectedLocations) => {
      return toggleLocationSelection(prevSelectedLocations, location);
    });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filterAndSortLocations = () => {
    const queryLocations = locations.features.filter((location) =>
      location.properties.NIL.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return queryLocations.sort((a, b) =>
      a.properties.NIL.localeCompare(b.properties.NIL, undefined, {
        sensitivity: "base",
      })
    );
  };

  const filteredLocations = filterAndSortLocations();

  const handleSearch = async () => {
    const geometries = selectedLocations.map((location) => location.geometry);
    const zoneIds = selectedLocations.map(
      (location) => location.properties.FID_1
    );

    const mapLocations = selectedLocations.map((location) => {
      const cityPart = location.properties?.City
        ? `, ${location.properties.City}`
        : "";
      return `${capitalizeWord(location.properties.NIL)}${cityPart}`;
    });

    try {
      const filters = {
        propertyType: formatPropertyType(
          selectedPropertyType,
          propertyTypeDetails
        ),
        polygons: geometries,
        sortBy: sortBy.value,
      };

      const res = await searchProperties(filters);
      if (res) {
        closeSearchDropdown();

        const queryParams = {
          propertyType: selectedPropertyType,
          zoneIds,
          sortBy: sortBy.label,
          propertyTypeDetails,
          polygons: geometries,
          mapLocations: mapLocations,
        };

        const url = buildResultsPageUrl(queryParams);

        navigation(url);
      }
    } catch (error) {
      console.log("Search property error: ", error);
    }
  };
  return (
    <div className="w-full md:w-[600px] absolute md:relative flex top-0 right-0 left-0 z-50 flex-col bg-[#f5f6f7] h-full overflow-hidden max-h-screen p-[14px] shadow-lg">
      <div className="bg-white shadow-sm">
        <div className="flex justify-between h-[70px] items-center px-5 border-b border-[#d5d7dd]">
          <h1 className="text-[25px] font-semibold">Zone su mappa</h1>
          <CloseIcon
            onClick={() => navigation("/")}
            strokeWidth={1}
            className="cursor-pointer w-[20px] h-[20px]"
          />
        </div>
        {selectedLocations.length > 0 && (
          <div className="flex justify-between h-[70px] items-center px-5 border-b border-[#d5d7dd]">
            <div className="flex px-[7px] py-[4px] cursor-pointer items-center space-x-2 bg-[#456AF21A] max-w-[400px] rounded-[5px] h-[40px]">
              <p className="text-[#456AF2] text-[18px] truncate flex-1 font-[200] ">
                {selectedLocations.length > 1
                  ? `Milano • ${selectedLocations.length} zone selezionate`
                  : `${capitalizeWord(
                      selectedLocations[0]?.properties?.NIL
                    )} • ${selectedLocations[0]?.properties?.City || "Milano"}`}
              </p>
              <div
                onClick={() => setSelectedLocations([])}
                className="!w-[14px] min-w-[14px] !h-[14px] rounded-full bg-[#EFE9F6] flex items-center justify-center"
              >
                <CloseIcon width={8} height={8} fill="#456AF2" />
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-between h-[70px] items-center px-5">
          <input
            type="text"
            className="outline-none text-lg w-full border-none h-full"
            placeholder="Cerca o seleziona zona"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
      </div>
      {mapType !== "draw" && (
        <div className="bg-white shadow-sm flex flex-col h-full mt-[14px] overflow-y-auto w-full rounded-bl-[12px] rounded-br-[12px]">
          <div className="flex items-center justify-between p-5 sticky bg-white top-0 w-full z-10">
            <h1 className="text-[20px] font-normal">Zone</h1>
            <h3
              onClick={() => setViewMap(!viewMap)}
              className="text-[20px] block md:hidden font-normal text-primary cursor-pointer"
            >
              {viewMap ? "Vedi Elenco" : "Vedi Mappa"}
            </h3>
          </div>
          {viewMap ? (
            <BaseMap
              mapStyle={{ zIndex: 1, height: "100%", flexGrow: 1 }}
              zoomLevel={11}
            >
              <GeoJSONMap
                selectedLocations={selectedLocations}
                onLocationSelect={handleMapLocationSelect}
              />
            </BaseMap>
          ) : filteredLocations.length > 0 ? (
            <div>
              {filteredLocations.map((location) => {
                const { FID_1, NIL } = location.properties;
                const isChecked = selectedLocations.some(
                  (loc) => loc.properties.FID_1 === FID_1
                );

                return (
                  <div
                    key={FID_1}
                    className="hover:bg-[#F4F4F4] px-5 relative transition-all duration-200 ease-in-out h-[52px]"
                  >
                    <SquireCheckbox
                      key={FID_1}
                      id={FID_1}
                      checked={isChecked}
                      label={capitalizeWord(NIL)}
                      onChange={() => handleSelectLocation(location)}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-start px-5 text-[18px] text-slate-600  py-5">
              Nessun risultato
            </div>
          )}
        </div>
      )}

      {isMobile && mapType === "draw" && (
        <BaseMap
          mapStyle={{ zIndex: 1, height: "100%", flexGrow: 1 }}
          zoomLevel={11}
        >
          <DrawMap
            onLocationSelect={handleMapLocationSelect}
            onLocationRemove={handleLocationRemove}
            setDrawLocationName={setDrawLocationName}
          />
        </BaseMap>
      )}
      {selectedLocations.length > 0 && (
        <button
          disabled={loading}
          onClick={handleSearch}
          className="h-[52px] min-h-[52px] disabled:cursor-not-allowed uppercase bg-[#1DA1F2] rounded-[12px] mt-[14px] text-white w-full text-center text-lg flex items-center justify-center"
        >
          {loading ? <ButtonSpinner /> : "Cerca"}
        </button>
      )}
    </div>
  );
};

export default MapLeftSidebar;
